import { useState, useEffect } from 'react';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';
import type { FieldIssue } from '../../types';
import {
  CharCount,
  FieldLabel,
  InlineIssue,
  Field,
  UploadZone,
  AssetPreview,
  AssetPreviewFromUrl,
  inputClass,
  issueBorder,
} from './shared';
import type { Campaign, ValidationFeedback } from '@/api/campaigns';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const HEADLINE_LIMIT = 34;

// ─── Main component ───────────────────────────────────────────────────────────

interface SnapchatDetailsInitialValues {
  headline?: string;
  url?: string;
  legal?: string;
  existingImageUrl?: string;
  imageContentType?: string | null;
}

interface SnapchatDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  file9x16: File | null;
  onFile9x16: (f: File | null) => void;
  onHeadlineChange: (v: string) => void;
  onUrlChange: (v: string) => void;
  onLegalChange: (v: string) => void;
  onExistingAssetClear?: () => void;
  initialValues?: SnapchatDetailsInitialValues;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function SnapchatDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  file9x16,
  onFile9x16: setFile9x16,
  onHeadlineChange,
  onUrlChange,
  onLegalChange,
  onExistingAssetClear,
  initialValues,
  feedback,
  feedbackPending,
  onAIFeedback,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: SnapchatDetailsProps) {
  const [headline, setHeadline] = useState(initialValues?.headline ?? '');
  const [destinationUrl, setDestinationUrl] = useState(initialValues?.url ?? '');
  const [legal, setLegal] = useState(initialValues?.legal ?? '');
  const [clearedAsset, setClearedAsset] = useState(false);
  const [pendingClear, setPendingClear] = useState(false);

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);

  // Sync initial values to parent on mount
  useEffect(() => {
    onHeadlineChange(headline);
    onUrlChange(destinationUrl);
    onLegalChange(legal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Confirmation modal for deleting saved asset */}
      {pendingClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-80 bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Delete Asset
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                Are you sure you want to remove the saved asset? You will still need to press Save
                to apply this change.
              </p>
              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={() => setPendingClear(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setClearedAsset(true);
                    onExistingAssetClear?.();
                    setPendingClear(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
        {/* ── About ───────────────────────────────────────────────────── */}
        <div className="border-l-2 border-[#0014cc]/30 pl-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
            About this format
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            A full-screen <span className="font-medium text-slate-700">9:16 image or video</span>{' '}
            that appears between friends' stories on Snapchat. The headline overlays the bottom of
            the ad in a semi-transparent info box, and users swipe up to reach the destination URL.{' '}
            <a
              href="https://forbusiness.snapchat.com/advertising/ad-formats"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
            >
              Snapchat ad formats ↗
            </a>
          </p>
        </div>

        {/* ── Gear Exchange Import ──────────────────────────────────── */}
        {
          <>
            <GearExchangeImportButton
              totalAssets={gxData?.total_assets ?? null}
              loading={gxLoading}
              error={gxError}
              fordshareIds={fordshareIds ?? undefined}
              onClick={() => setShowGxModal(true)}
            />

            {showGxModal && gxData && campaign && campaignId && (
              <GearExchangeModal
                data={gxData}
                campaign={campaign}
                campaignId={campaignId}
                formatLabel="Snapchat"
                zoneLimits={[
                  {
                    ratio: '9x16',
                    limit: 1,
                    existing:
                      file9x16 || (initialValues?.existingImageUrl && !clearedAsset) ? 1 : 0,
                  },
                ]}
                onClose={() => setShowGxModal(false)}
                usedInVariants={usedInVariants}
                onImport={(files: GXImportedFile[]) => {
                  const file = files[0];
                  if (file) {
                    setFile9x16(file.file);
                    setClearedAsset(true);
                  }
                }}
              />
            )}
          </>
        }

        {/* ── Asset ───────────────────────────────────────────────────── */}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
            9:16 Asset
          </p>
          <div className="border-b border-slate-200 mb-3" />
          {(() => {
            const fi = fieldIssues['asset_files'];
            const it =
              fi?.errors.length && showErrors
                ? ('error' as const)
                : fi?.warnings.length && showWarnings
                  ? ('warning' as const)
                  : undefined;
            return (
              <>
                {file9x16 ? (
                  <AssetPreview
                    file={file9x16}
                    onRemove={() => setFile9x16(null)}
                    ratio="9x16"
                    issueType={it}
                  />
                ) : initialValues?.existingImageUrl && !clearedAsset ? (
                  <AssetPreviewFromUrl
                    url={initialValues.existingImageUrl}
                    contentType={initialValues.imageContentType}
                    onRemove={() => setPendingClear(true)}
                    ratio="9x16"
                    issueType={it}
                  />
                ) : (
                  <UploadZone
                    onFile={setFile9x16}
                    hint="Click to upload 9:16 image or video"
                    issueType={it}
                  />
                )}
                <InlineIssue
                  issue={fi}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                  feedback={feedback}
                  feedbackPending={feedbackPending}
                  onFeedback={onAIFeedback}
                />
              </>
            );
          })()}
        </div>

        {/* ── Copy ────────────────────────────────────────────────────── */}
        <div className="space-y-5">
          {/* Headline */}
          <div>
            <FieldLabel
              label="Headline"
              right={<CharCount value={headline} limit={HEADLINE_LIMIT} />}
              tooltip="Shown in the footer info box on the ad. Keep it short and action-oriented."
            />
            <input
              type="text"
              value={headline}
              onChange={(e) => {
                setHeadline(e.target.value);
                onHeadlineChange(e.target.value);
              }}
              placeholder="Short, punchy headline..."
              className={`${inputClass} ${issueBorder(fieldIssues['headline'], showErrors, showWarnings)}`}
            />
            <InlineIssue
              issue={fieldIssues['headline']}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />
          </div>

          {/* Destination URL */}
          <div>
            <FieldLabel
              label="Destination URL"
              tooltip="Where users are taken when they swipe up. Make sure it matches the offer in your creative."
            />
            <input
              type="url"
              value={destinationUrl}
              onChange={(e) => {
                setDestinationUrl(e.target.value);
                onUrlChange(e.target.value);
              }}
              placeholder="https://www.ford.com/..."
              className={`${inputClass} ${issueBorder(fieldIssues['destination_url'], showErrors, showWarnings)}`}
            />
            <InlineIssue
              issue={fieldIssues['destination_url']}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />
          </div>

          {/* Legal */}
          <Field
            label="Legal"
            tooltip="Required in some markets (e.g. France) for regulated categories. Add any mandatory disclaimer copy here — it will not appear in the mockup but will be submitted with the ad."
            fieldKey="legal"
            fieldIssues={fieldIssues}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
            help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
          >
            <textarea
              value={legal}
              onChange={(e) => {
                setLegal(e.target.value);
                onLegalChange(e.target.value);
              }}
              placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
              rows={5}
              className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
            />
          </Field>
        </div>
      </div>
    </>
  );
}
